import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useSyncExternalStore } from 'react';

import { useServices } from '../app/services-context';
import { cssVar } from '../theme/tokens';
import type { ToastSeverity } from './toast-store';

const ICON = {
  success: <CheckCircleRoundedIcon />,
  info: <InfoRoundedIcon />,
  warning: <WarningRoundedIcon />,
  error: <ErrorRoundedIcon />,
} as const satisfies Record<ToastSeverity, unknown>;

const ROLE = { success: 'success', info: 'info', warning: 'warning', error: 'danger' } as const;

/**
 * Shows queued toasts one at a time, bottom centre (DESIGN-V3): a navy pill with the status icon
 * in its readable tint. A `data-theme="dark"` scope in both schemes; errors stay longer.
 */
export function ToastViewport() {
  const { toasts } = useServices();
  const queue = useSyncExternalStore(toasts.subscribe, toasts.getSnapshot);
  const current = queue[0];
  if (!current) return null;

  return (
    <Snackbar
      key={current.id}
      open
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={current.severity === 'error' ? 8000 : 5000}
      onClose={(_event, reason) => {
        if (reason !== 'clickaway') toasts.dismiss(current.id);
      }}
    >
      <Alert
        data-theme="dark"
        severity={current.severity}
        icon={ICON[current.severity]}
        onClose={() => toasts.dismiss(current.id)}
        sx={{
          // Above the theme's status colours for standard alerts.
          '&&': {
            alignItems: 'center',
            width: '100%',
            px: 4.5,
            py: 1,
            borderRadius: cssVar('radius-lg'),
            bgcolor: cssVar('secondary'),
            color: cssVar('text'),
            boxShadow: cssVar('shadow-lg'),
            typography: 'bodySm',
            fontWeight: 600,
          },
          '&& .MuiAlert-icon': { color: cssVar(`${ROLE[current.severity]}-text`), mr: 2.5 },
          '& .MuiAlert-action .MuiIconButton-root': { color: cssVar('text-muted') },
        }}
      >
        {current.message}
      </Alert>
    </Snackbar>
  );
}

export function useToast() {
  const { toasts } = useServices();
  const show = (message: string, severity: ToastSeverity = 'info') =>
    toasts.show(message, severity);
  return {
    show,
    success: (message: string) => show(message, 'success'),
    info: (message: string) => show(message, 'info'),
    warning: (message: string) => show(message, 'warning'),
    error: (message: string) => show(message, 'error'),
  };
}
